import { action, computed, observable } from 'mobx';
import Alert from 'react-s-alert';

import { analytics } from 'core/analytics';
import { AppManager } from 'core/appManager';

import {
  NotificationsQuery,
  NotificationsPollQuery,
  MarkAllAsReadMutation,
} from 'apps/notificationCenter/gql';
import { NotificationConnManager } from 'apps/notificationCenter/connManager';

const NOTIFICATIONS_POLL_INTERVAL = 10000;

export class NotificationCenterAppManager extends AppManager {
  static registryName = 'NotificationCenterAppManager';
  @observable hasError = false;
  @observable pendingCount = 0;

  constructor(parent, init = {}) {
    super(parent, init);

    this.notificationsConn = new NotificationConnManager(this, {
      initialBatchSize: 10,
      batchSize: 30,
      sortBy: 'recent',
      sortOptions: [],
      loadMoreQuery: NotificationsQuery,
    });
  }

  @computed get notifications() {
    return this.notificationsConn ? this.notificationsConn.arrayApp || [] : [];
  }

  @computed get polling() {
    return !!this.pollNotificationsQuery;
  }

  @action CatchError = error => {
    this.hasError = true;
    analytics.logError(error);
  };

  @action NewNotificationsPollQuery = () => {
    this.pollNotificationsQuery = new NotificationsPollQuery(this, {
      queryKey: 'notificationsPoll',
      pollInterval: NOTIFICATIONS_POLL_INTERVAL,
      fetchPolicy: 'network-only',
    });
    this.pollNotificationsQuery.process(
      result => (result ? result.pendingCount : 0),
      this.SetPendingCount,
    );
    this.pollNotificationsQuery.query();
  };

  @action StopNotificationsPollQuery = () => {
    if (this.pollNotificationsQuery) {
      this.pollNotificationsQuery.disposer();
      this.pollNotificationsQuery = null;
    }
  };

  @action SetPendingCount = pendingCount => {
    const count = parseInt(pendingCount, 10);
    if (typeof count === 'number' && count >= 0) {
      this.pendingCount = count;
    }
  };

  @action LoadMore = () => {
    this.notificationsConn.loadMore({ subPath: 'notifications' });
  };

  @action NewNotificationsQuery = () => {
    this.notificationsConn.Reset();
    this.notificationsConn.loadingMore = true;
    this.notificationsQuery = new NotificationsQuery(this.notificationsConn, {
      fetchPolicy: 'network-only',
    });
    this.notificationsQuery.process(
      this.notificationsConn.process,
      this.notificationsConn.moreData,
      { subPath: 'notifications' },
    );
    this.notificationsQuery.process(result => result, this.SetPendingCount, {
      subPath: 'pendingCount',
    });
    this.notificationsQuery.query();
  };

  @computed get loading() {
    return !this.notificationsConn || this.notificationsConn.loadingMore;
  }

  @action MarkAllNotificationsAsRead = () => {
    const options = {
      onSysErr: () =>
        Alert.error(
          'Oops, there was an error marking your notifications as read.',
        ),
      onUserErr: () =>
        Alert.error(
          'Oops, there was an error marking your notifications as read.',
        ),
      onSuccess: pendingCount => {
        this.pendingCount = pendingCount;
        this.notificationsConn.arrayApp.apps.forEach(notification => {
          notification.SetRead(true);
        });
        Alert.success('All Notifications marked as read.');
      },
    };
    const mutation = new MarkAllAsReadMutation();
    mutation.setCallbacks(options);
    mutation.mutate();
    return mutation;
  };
}

export const notificationCenter = new NotificationCenterAppManager();
